import * as React from "react";
import { withRouter, RouteComponentProps, Redirect } from "react-router-dom";

import { HAS_USER_LOGGED_OUT } from "@config/consts";

import { AuthGuardState } from "@typings/locations";
import { AUTH_REDIRECTS } from "@typings/globals";

import { stringToBool } from "@services/StringToBool";
import { sessionStorageFacade } from "@services/SessionStorageFacade";

import { LoginModalComponent } from "@hooks/useAuth/components/Login";
import { useAuth } from "@hooks/useAuth";
import { useLocalisation } from "@hooks/useLocalisation";

import { RedirectionScreen } from "@app/pages/Redirection/RedirectionScreen";

interface Props extends RouteComponentProps<null, any, AuthGuardState> {
  children: React.ReactNode;
}

export const RedirectionComponent = (
  props: Props,
): React.ReactElement | null => {
  const { isUserStateKnown, isUserLoggedIn } = useAuth();
  const { language } = useLocalisation();

  const sessionStorage = sessionStorageFacade();
  const state = props.history.location.state;
  const locationState = props.location.state;

  const hasUserLoggedOut = stringToBool(
    sessionStorage.getItem(HAS_USER_LOGGED_OUT) || "",
  );

  const handleLoginSuccess = () => {
    props.history.push(locationState.originalRoute);
  };

  const handleLoginCloseRequest = () => {
    window.location.href = `/${language}/`;
  };

  React.useEffect(() => {
    // redirect user to the homepage when he just logged out intentionally (in PWA)
    if (hasUserLoggedOut) {
      sessionStorage.setItem(HAS_USER_LOGGED_OUT, "false");
      window.location.href = "/";
    }
  }, []);

  if (!isUserStateKnown) {
    return null;
  }

  if (locationState && locationState.originalRoute && !hasUserLoggedOut) {
    return (
      <React.Fragment>
        <LoginModalComponent
          onModalLoginSuccess={handleLoginSuccess}
          onModalCloseRequest={handleLoginCloseRequest}
        />
        <RedirectionScreen />
      </React.Fragment>
    );
  }

  if (
    state &&
    state.referer &&
    state.reason === AUTH_REDIRECTS.UNAUTHENTICATED &&
    isUserLoggedIn
  ) {
    return (
      <Redirect
        to={{
          pathname: state.referer,
          search: props.location ? props.location.search : "",
        }}
        push={false}
      />
    );
  }

  return <RedirectionScreen />;
};

export const Redirection = withRouter(RedirectionComponent);
